import { useId, type FormEvent } from "react";
import type { RateGroup, RateItem, RateItemInput } from "@/api/clients";
import { RequestError } from "@/components/RequestError";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type RateItemFormValues = {
  item_no: string;
  code: string;
  rate_group: RateGroup;
  description: string;
  detail: string;
  unit_rate: string;
  currency: string;
  auto_include: boolean;
  sort_order: string;
};

const GROUP_OPTIONS = [
  { value: "A", label: "A - billed on every vessel call" },
  { value: "B", label: "B - only when requested" },
];

export function toRateItemInput(values: RateItemFormValues): RateItemInput {
  return {
    item_no: Number.parseInt(values.item_no, 10),
    code: values.code.trim(),
    rate_group: values.rate_group,
    description: values.description.trim(),
    detail: values.detail.trim(),
    unit_rate: values.unit_rate.trim(),
    currency: values.currency.trim() || null,
    auto_include: values.rate_group === "A" && values.auto_include,
    sort_order: Number.parseInt(values.sort_order, 10) || 0,
  };
}

type RateItemFormProps = {
  initial?: RateItem | null;
  clientCurrency: string;
  submitLabel?: string;
  onSubmit: (input: RateItemInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function RateItemForm({
  initial,
  clientCurrency,
  submitLabel = "Save item",
  onSubmit,
  onCancel,
  onReload,
}: RateItemFormProps) {
  const id = useId();
  const { values, set, setValues } = useFormValues<RateItemFormValues>({
    item_no: initial ? String(initial.item_no) : "",
    code: initial?.code ?? "",
    rate_group: initial?.rate_group ?? "A",
    description: initial?.description ?? "",
    detail: initial?.detail ?? "",
    unit_rate: initial?.unit_rate ?? "",
    currency: initial && initial.currency !== clientCurrency ? initial.currency : "",
    auto_include: initial?.auto_include ?? false,
    sort_order: String(initial?.sort_order ?? 0),
  });
  const { error, errors, saving, run } = useSubmitState();
  const groupA = values.rate_group === "A";

  function changeGroup(group: string): void {
    const rateGroup: RateGroup = group === "B" ? "B" : "A";
    setValues((current) => ({
      ...current,
      rate_group: rateGroup,
      auto_include: rateGroup === "A" ? current.auto_include : false,
    }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit(toRateItemInput(values)));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-item-no`}
          label="Item number"
          type="number"
          inputMode="numeric"
          min={1}
          max={999}
          value={values.item_no}
          onChange={(value) => set("item_no", value)}
          error={errors.item_no}
          hint="Printed on the invoice line"
        />
        <SelectField
          id={`${id}-group`}
          label="Group"
          value={values.rate_group}
          onChange={changeGroup}
          options={GROUP_OPTIONS}
          error={errors.rate_group}
        />
        <TextField
          id={`${id}-description`}
          label="Description"
          value={values.description}
          onChange={(value) => set("description", value)}
          error={errors.description}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-detail`}
          label="Detail"
          value={values.detail}
          onChange={(value) => set("detail", value)}
          error={errors.detail}
          hint="Smaller second line, e.g. Item 1 - per terminal call"
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-rate`}
          label="Unit rate"
          inputMode="decimal"
          value={values.unit_rate}
          onChange={(value) => set("unit_rate", value)}
          error={errors.unit_rate}
          hint="Up to 2 decimals, e.g. 350.00"
        />
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={values.currency}
          onChange={(value) => set("currency", value)}
          error={errors.currency}
          placeholder={clientCurrency}
          hint={`Leave blank to use the client's currency (${clientCurrency})`}
          maxLength={3}
        />
        <TextField
          id={`${id}-code`}
          label="Code"
          value={values.code}
          onChange={(value) => set("code", value)}
          error={errors.code}
          hint="Optional internal code"
        />
        <TextField
          id={`${id}-sort`}
          label="Sort order"
          type="number"
          inputMode="numeric"
          value={values.sort_order}
          onChange={(value) => set("sort_order", value)}
          error={errors.sort_order}
        />
      </div>
      <Checkbox
        id={`${id}-auto`}
        label="Add to every invoice automatically"
        checked={groupA && values.auto_include}
        disabled={!groupA}
        onChange={(checked) => set("auto_include", checked)}
        hint={groupA ? undefined : "Only group A items can be added automatically."}
      />
      {errors.auto_include && <p className="text-xs text-destructive">{errors.auto_include}</p>}
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
